import { clamp, includes, reduce } from 'ramda';

import {
  actions as bootstrapActions,
  constants as bootstrapConstants,
  selectors as bootstrapSelectors,
} from '@ezugi/bootstrap';
import { LAST_BETS, PLACE_YOUR_BETS } from '@ezugi/constants';

import * as BET_TYPES from '../../../../constants/BackAndLay/betTypes';
import { LAY_BETS } from '../../../../constants/BackAndLay/betTypes';
import { currentBetsSelector } from '../../../selectors/bets';
import { STATUS } from '../../bets/constants';

const {
  currentLimitsSelector,
  generalConfigSelector,
  payoutsSelector,
  totalBetSelector,
  userBalanceSelector,
} = bootstrapSelectors;
const { dialogActions: { dialog }, notificationActions: { notification } } = bootstrapActions;

const { DIALOG: { LOW_BALANCE_DIALOG } = {} } = bootstrapConstants;

const RESULT_SEED = { ok: true, valid: true, status: STATUS.VALID };

export const getSimpleNotificationPayload = ({ status, value, betAutoAdjust, maxTableLimit, maxBet, minBet }) => {
  const payload = (() => {
    switch (status) {
    case STATUS.NOT_ALLOWED:
      return { message: 'notifications.bet_not_allowed' };
    case STATUS.INVALID_BALANCE:
      return { message: 'messages.low_balance_message' };
    case STATUS.OVER_TABLE_LIMIT:
      return value
        ? { message: 'notifications.bet_over_table_max_limit_adjusted' }
        : { message: 'notifications.bet_over_table_max_limit', variables: { maxTableLimit } };
    case STATUS.OVER_INDEX_LIMIT:
      return { message: 'notifications.bet_over_index_max_limit_adjusted', variables: { maxIndexLimit: maxBet } };
    case STATUS.BELOW_INDEX_LIMIT:
    case STATUS.BELOW_TABLE_LIMIT:
      return betAutoAdjust
        ? {
          autoAdjust: true,
          // eslint-disable-next-line camelcase
          event_name: STATUS.BELOW_INDEX_LIMIT,
          message: 'notifications.bet_below_index_min_limit_adjusted',
        }
        : { message: 'notifications.bet_below_index_min_limit', variables: { minIndexLimit: minBet } };
    case STATUS.REBETTING_NOT_ALLOWED:
      return { message: 'notifications.rebet_not_allowed' };
    default:
      return null;
    }
  })();
  return payload;
};

const pipeValidations = (validations) => (bet, state) => reduce(
  (res, fn) => (!res.valid ? res : fn(res.bet, state)),
  {
    ...RESULT_SEED,
    bet,
  },
  validations
);

export const getBetTypeLimits = (() => {
  const cache = {};
  let currentLimits;

  return (type, state) => {
    if (cache[type]) {
      return cache[type];
    }

    currentLimits = currentLimits || currentLimitsSelector(state);

    const limits = { min: 0, max: 0 };

    switch (type) {
    /* BACK BETS */
    case BET_TYPES.BACK_BETS.PLAYER_A_BACK:
    case BET_TYPES.BACK_BETS.PLAYER_B_BACK: {
      limits.min = currentLimits.Min_Bet;
      limits.max = currentLimits.Max_Bet;
      break;
    }
    /* LAY BETS */
    case BET_TYPES.LAY_BETS.PLAYER_A_LAY:
    case BET_TYPES.LAY_BETS.PLAYER_B_LAY: {
      limits.min = currentLimits.Min_Group1;
      limits.max = currentLimits.Max_Group1;
      break;
    }
    default:
      break;
    }
    return limits;
  };
})();

export const validateRoundStatus = (bet, { round: { roundStatus } }) => {
  const ok = includes(roundStatus, [ PLACE_YOUR_BETS, LAST_BETS ]);
  return {
    ok,
    valid: ok,
    status: ok ? STATUS.VALID : STATUS.BETTING_NOT_ALLOWED,
    bet,
    actions: [],
  };
};

export const validateBalance = (bet, state) => {
  const balance = userBalanceSelector(state);
  const totalBet = totalBetSelector(state);
  const payouts = payoutsSelector(state);

  const odd = payouts[bet.type].value;
  const betValue = Object.values(LAY_BETS).includes(bet.type)
    ? +(odd * bet.value).toFixed(2)
    : bet.value;

  const ok = balance - totalBet >= betValue;
  const status = ok ? STATUS.VALID : STATUS.INVALID_BALANCE;

  return {
    ok,
    valid: ok,
    status,
    bet,
    actions: [ dialog.add({ name: LOW_BALANCE_DIALOG }) ],
  };
};

export const validateLimits = (bet, state) => {
  // bet limits
  const currentBets = currentBetsSelector(state);
  const { min: minBet, max: maxBet } = getBetTypeLimits(bet.type, state);
  const existingBet = (currentBets[bet.type] || {}).value || 0;

  const { betAutoAdjust } = generalConfigSelector(state);
  let status = bet.value + existingBet < minBet
    ? STATUS.BELOW_INDEX_LIMIT
    : bet.value + existingBet > maxBet
      ? STATUS.OVER_INDEX_LIMIT
      : STATUS.VALID;

  let ok = status === STATUS.VALID;
  const diff = maxBet - existingBet;

  let value = ok
    ? bet.value
    : clamp(betAutoAdjust ? minBet - existingBet : bet.value > diff ? diff : bet.value, diff, bet.value);

  // table limits
  const { Max_Bet: maxTableLimit } = currentLimitsSelector(state);

  const totalBet = totalBetSelector(state);

  // totalBet + value > maxTableLimit
  if (value > maxTableLimit) {
    status = STATUS.OVER_TABLE_LIMIT;
    ok = false;
    value = maxTableLimit - totalBet;
  }

  // TODO: decide if below check is needed here
  /* const balance = userBalanceSelector(state);
    if (value > balance - totalBet) {
    status = STATUS.INVALID_BALANCE;
    ok = false;
    value = 0;
  } */

  const valid = !includes(status, [ STATUS.INVALID_BALANCE, STATUS.BETTING_NOT_ALLOWED ]);

  const notificationPayload = status !== STATUS.INVALID_BALANCE
    ? getSimpleNotificationPayload({ status, value, betAutoAdjust, minBet, maxBet, maxTableLimit })
    : null;

  const _actions = [
    ...(status === STATUS.INVALID_BALANCE
      ? [ dialog.add({ name: LOW_BALANCE_DIALOG }) ]
      : [ // there is an issue with getSimpleNotificationPayload() returning null in case of valid bet
        ...(notificationPayload ? [ notification.add(notificationPayload) ] : []),
      ]),
  ];

  return {
    ok,
    valid,
    status,
    bet: {
      ...(value && {
        ...bet,
        value,
        valid: !(status === STATUS.BELOW_INDEX_LIMIT && !betAutoAdjust && value + existingBet < minBet),
      }),
    },
    actions: _actions,
  };
};

export const validateBet = pipeValidations([ validateRoundStatus, validateBalance, validateLimits ]);
